import { useCallback, useEffect, useRef, useState } from "react";
import type { WorkflowRunView } from "@dentacore/shared-types";

import { getPatient } from "../api/patients";
import {
  readRingOrders,
  resetRingOrder,
  ringWorkflows,
  saveRingOrder,
  startRingRun,
} from "../api/workflows";
import { useCan } from "../auth/use-can";
import { formatMoney } from "../patients/money";
import { useModules } from "./modules-context";
import { findRing, type RingAction, type RingSpec } from "./ring/ring-registry";
import { RING_ICONS } from "./ring/ring-icons";
import { RingRunPanel } from "./ring/ring-run-panel";
import styles from "./quick-ring.module.css";

/**
 * **The quick ring** — a circular menu opened where the pointer is, so nothing
 * needs hunting for in a sidebar or a panel. An accessibility tool first: every
 * action is one gesture away and each target is a wedge, not a small button.
 *
 * - **Ctrl + click** opens it at the pointer (QR1). On a Mac, Ctrl + click
 *   arrives as a right-click, so that is caught too. A plain right-click keeps
 *   the browser's own menu.
 * - **Ctrl + .** opens it on whatever has focus, for anyone not using a mouse.
 * - Inside: **1–9** run an action, the **arrow keys** move round it, **Enter**
 *   runs the lit one, **Escape** closes it and puts focus back.
 *
 * **The ring belongs to the thing under the pointer** (QR7, STORY-221): a
 * booking, an empty slot, a patient, a tooth, an item, a task… Each screen
 * says what its things are through `useRingProvider`; blank space opens the
 * screen's own ring, and a screen with none (System) opens nothing (WG-D18).
 * **It never acts on someone not pointed at** (supersedes QR4).
 *
 * Only actions the reader may take are drawn (QR5) — a permission not held, or
 * an add-on not switched on, hides the action; the server refuses regardless.
 * At most eight wedges: past that the last is *More…*, listing the rest (WG-D18).
 *
 * **A ring about a patient shows their critical alert and what they owe** under
 * it (QR10). **Each person may arrange their own ring** (QR11): the order is
 * kept per person and per ring on the server (WG-D19), and an action they never
 * placed keeps its default place after theirs. **Workflows that start from the
 * ring** are wedges after the thing's own actions, for their roles (WF-D3,
 * STORY-225); one opens a panel that walks the run.
 */

/** Each person's own orders, read once and kept for the session (WG-D19). */
let ordersCache: Record<string, string[]> | null = null;

/** The thing's actions in the person's order: theirs first, the rest where they were (QR11). */
function arranged(actions: RingAction[], order: string[] | undefined): RingAction[] {
  if (order === undefined || order.length === 0) return actions;
  const byKey = new Map(actions.map((a) => [a.key, a]));
  const placed = order.flatMap((key) => {
    const action = byKey.get(key);
    return action === undefined ? [] : [action];
  });
  return [...placed, ...actions.filter((a) => !order.includes(a.key))];
}

interface PatientLine {
  critical: string | null;
  owes: string | null;
}

const SIZE = 320;
const OUTER = 156;
const INNER = 58;
/** Wedges before the last becomes *More…* (WG-D18). */
const MAX_WEDGES = 8;

/** Ask the ring to show a line under it — for a provider whose action finishes later. */
export function announceRing(text: string): void {
  window.dispatchEvent(new CustomEvent<string>("dentacore:ring-notice", { detail: text }));
}

/** The wedge for slot `i` of `n`, centred on its angle, starting at the top and going clockwise. */
function wedge(i: number, n: number): string {
  const c = SIZE / 2;
  const gap = 0.014;
  const a0 = (i / n) * Math.PI * 2 - Math.PI / 2 - Math.PI / n + gap;
  const a1 = ((i + 1) / n) * Math.PI * 2 - Math.PI / 2 - Math.PI / n - gap;
  const p = (r: number, a: number) =>
    `${(c + r * Math.cos(a)).toFixed(1)} ${(c + r * Math.sin(a)).toFixed(1)}`;
  // A ring of one is a whole circle, which an arc cannot draw end to end.
  if (n === 1) {
    return `M ${String(c)} ${String(c - OUTER)} A ${String(OUTER)} ${String(OUTER)} 0 1 1 ${String(c - 0.01)} ${String(c - OUTER)} Z M ${String(c)} ${String(c - INNER)} A ${String(INNER)} ${String(INNER)} 0 1 0 ${String(c - 0.01)} ${String(c - INNER)} Z`;
  }
  return `M ${p(OUTER, a0)} A ${String(OUTER)} ${String(OUTER)} 0 0 1 ${p(OUTER, a1)} L ${p(INNER, a1)} A ${String(INNER)} ${String(INNER)} 0 0 0 ${p(INNER, a0)} Z`;
}

function centreOf(i: number, n: number): { x: number; y: number } {
  const a = (i / n) * Math.PI * 2 - Math.PI / 2;
  const r = (OUTER + INNER) / 2 + 4;
  return { x: SIZE / 2 + r * Math.cos(a), y: SIZE / 2 + r * Math.sin(a) };
}

export function QuickRing(): React.JSX.Element | null {
  const can = useCan();
  const modules = useModules();
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const [spec, setSpec] = useState<RingSpec | null>(null);
  const [active, setActive] = useState(0);
  const [more, setMore] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [extra, setExtra] = useState<RingAction[]>([]);
  const [order, setOrder] = useState<string[] | undefined>(undefined);
  const [arranging, setArranging] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [patient, setPatient] = useState<PatientLine | null>(null);
  const [run, setRun] = useState<{ view: WorkflowRunView; actions: RingAction[] } | null>(null);
  const ring = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const noticeTimer = useRef(0);

  const say = useCallback((text: string) => {
    setNotice(text);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 4200);
  }, []);

  useEffect(() => {
    const onNotice = (event: Event) => say((event as CustomEvent<string>).detail);
    window.addEventListener("dentacore:ring-notice", onNotice);
    return () => window.removeEventListener("dentacore:ring-notice", onNotice);
  }, [say]);

  /** What the reader may actually do here: permission held, add-on on. */
  const allowed = useCallback(
    (action: RingAction | false | null | undefined): action is RingAction =>
      typeof action === "object" &&
      action !== null &&
      (action.needs ?? []).every((p) => can(p)) &&
      (action.module === undefined || modules.writable(action.module)) &&
      (action.preview === undefined || modules.hasPreview(action.preview)),
    [can, modules],
  );

  const open = useCallback(
    (x: number, y: number, under: Element | null) => {
      const focused = document.activeElement as HTMLElement | null;
      void (async () => {
        let found: RingSpec | null = null;
        try {
          found = await findRing(under);
        } catch {
          found = null;
        }
        if (found === null) return;
        // Its workflows, after its own actions (WF-D3). A failed read offers none.
        let workflows: RingAction[] = [];
        const subject = found.subject;
        if (subject != null && modules.hasPreview("WORKFLOWS")) {
          try {
            workflows = (await ringWorkflows(subject.type)).map((w) => ({
              key: `wf:${w.id}`,
              label: w.name,
              icon: RING_ICONS.send,
              hint: `Workflow · ${String(w.stepCount)} step${w.stepCount === 1 ? "" : "s"}`,
              run: () => {
                const own = found.actions.filter(allowed);
                startRingRun(w.id, subject.id)
                  .then((view) => setRun({ view, actions: own }))
                  .catch((caught: unknown) =>
                    say(caught instanceof Error ? caught.message : "The workflow didn't start"),
                  );
              },
            }));
          } catch {
            workflows = [];
          }
        }
        // Nothing to offer here: no ring at all, rather than an empty one (WG-D18).
        if (!found.actions.some(allowed) && workflows.length === 0) return;
        if (ordersCache === null) {
          try {
            ordersCache = await readRingOrders();
          } catch {
            ordersCache = {};
          }
        }
        returnFocus.current = focused;
        setExtra(workflows);
        setOrder(ordersCache[found.ring]);
        setArranging(false);
        setPicked(null);
        setPatient(null);
        if (found.patientId != null && can("patients.view")) {
          const id = found.patientId;
          getPatient(id)
            .then((file) => {
              const flag = file.flags.find((f) => f.severity === "CRITICAL");
              const owed = (file.outstanding ?? []).filter((m) => m.cents > 0);
              setPatient({
                critical:
                  flag === undefined
                    ? null
                    : `${flag.label}${flag.detail ? `: ${flag.detail}` : ""}`,
                owes:
                  owed.length === 0
                    ? null
                    : owed.map((m) => formatMoney(m.cents, m.currency)).join(" + "),
              });
            })
            .catch(() => setPatient(null));
        }
        const half = SIZE / 2 + 8;
        setSpec(found);
        setActive(0);
        setMore(false);
        setAt({
          x: Math.min(Math.max(x, half), window.innerWidth - half),
          y: Math.min(Math.max(y, half), window.innerHeight - half),
        });
      })();
    },
    [allowed, can, modules, say],
  );

  const close = useCallback(() => {
    setAt(null);
    setMore(false);
    setArranging(false);
    returnFocus.current?.focus();
  }, []);

  // Ctrl + click, and Ctrl + . on the keyboard.
  useEffect(() => {
    const typing = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      el.closest("input, textarea, select, [contenteditable='true']") !== null;
    const outside = (el: EventTarget | null) =>
      el instanceof Element && el.closest("[role='dialog']") !== null;
    const fromPointer = (event: MouseEvent) => {
      if (!event.ctrlKey || typing(event.target) || outside(event.target)) return false;
      // Before the page's own handler, so a Ctrl + click on a link or a
      // calendar block opens the ring and nothing else.
      event.preventDefault();
      event.stopPropagation();
      return true;
    };
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || !fromPointer(event)) return;
      open(event.clientX, event.clientY, event.target as Element);
    };
    // A Mac turns Ctrl + click into a right-click: catch it there.
    const onContext = (event: MouseEvent) => {
      if (!fromPointer(event)) return;
      open(event.clientX, event.clientY, event.target as Element);
    };
    // The block's own pointer handlers would otherwise select it or start a drag.
    const swallow = (event: PointerEvent) => {
      if (event.ctrlKey && event.button === 0 && !typing(event.target) && !outside(event.target)) {
        event.stopPropagation();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (!event.ctrlKey || event.key !== ".") return;
      event.preventDefault();
      const focused = document.activeElement;
      const box =
        focused instanceof HTMLElement && focused !== document.body
          ? focused.getBoundingClientRect()
          : null;
      open(
        box === null ? window.innerWidth / 2 : box.left + box.width / 2,
        box === null ? window.innerHeight / 2 : box.top + box.height / 2,
        focused,
      );
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("contextmenu", onContext, true);
    document.addEventListener("pointerdown", swallow, true);
    document.addEventListener("pointerup", swallow, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("contextmenu", onContext, true);
      document.removeEventListener("pointerdown", swallow, true);
      document.removeEventListener("pointerup", swallow, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const all: RingAction[] = arranged(
    spec === null ? [] : [...spec.actions.filter(allowed), ...extra],
    order,
  );
  // Past eight, seven wedges and *More…* for the rest (WG-D18). While
  // arranging, every action is drawn, so any can be moved into the first seven.
  const overflow = !arranging && all.length > MAX_WEDGES ? all.slice(MAX_WEDGES - 1) : [];
  const actions: RingAction[] =
    overflow.length === 0
      ? all
      : [
          ...all.slice(0, MAX_WEDGES - 1),
          {
            key: "more",
            label: "More…",
            icon: RING_ICONS.list,
            hint: `${String(overflow.length)} more`,
            run: () => setMore(true),
          },
        ];

  /** Move the action at `from` to `to`, in the person's own order (QR11). */
  const moveAction = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0 || from >= all.length || to >= all.length) return;
    const keys = all.map((a) => a.key);
    const [key] = keys.splice(from, 1);
    if (key === undefined) return;
    keys.splice(to, 0, key);
    setOrder(keys);
    setActive(to);
  };

  const finishArranging = () => {
    setArranging(false);
    setPicked(null);
    if (spec === null || order === undefined) return;
    const ringName = spec.ring;
    ordersCache = { ...(ordersCache ?? {}), [ringName]: order };
    saveRingOrder(ringName, order).catch(() => say("Your order wasn't saved. Try again."));
  };

  const backToDefault = () => {
    if (spec === null) return;
    const ringName = spec.ring;
    setOrder(undefined);
    setPicked(null);
    const { [ringName]: _dropped, ...rest } = ordersCache ?? {};
    void _dropped;
    ordersCache = rest;
    resetRingOrder(ringName).catch(() => say("That didn't go through. Try again."));
  };

  const runAt = (i: number) => {
    const action = actions[i];
    if (action === undefined) return;
    if (arranging) {
      // Click an action, then where it goes (QR11).
      if (picked === null) setPicked(i);
      else {
        moveAction(picked, i);
        setPicked(null);
      }
      return;
    }
    if (action.key === "more") {
      action.run();
      return;
    }
    setAt(null);
    setMore(false);
    action.run();
  };

  const runOverflow = (action: RingAction) => {
    setAt(null);
    setMore(false);
    action.run();
  };

  useEffect(() => {
    if (at !== null) ring.current?.focus();
  }, [at]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const n = Math.max(actions.length, 1);
    if (arranging && event.shiftKey && event.key.startsWith("Arrow")) {
      event.preventDefault();
      const by = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
      moveAction(active, (active + by + n) % n);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      if (more) setMore(false);
      else if (arranging) finishArranging();
      else close();
    } else if (
      event.key === "ArrowRight" ||
      event.key === "ArrowDown" ||
      (event.key === "Tab" && !event.shiftKey)
    ) {
      event.preventDefault();
      setActive((i) => (i + 1) % n);
    } else if (
      event.key === "ArrowLeft" ||
      event.key === "ArrowUp" ||
      (event.key === "Tab" && event.shiftKey)
    ) {
      event.preventDefault();
      setActive((i) => (i - 1 + n) % n);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      runAt(active);
    } else if (/^[1-9]$/.test(event.key)) {
      event.preventDefault();
      runAt(Number(event.key) - 1);
    }
  };

  const lit = actions[active];

  return (
    <>
      {at !== null && spec !== null && (
        <div
          className={styles.scrim}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div
            ref={ring}
            className={styles.ring}
            style={{ left: at.x - SIZE / 2, top: at.y - SIZE / 2, width: SIZE, height: SIZE }}
            role="menu"
            aria-label={`Quick actions for ${spec.title}`}
            aria-activedescendant={lit === undefined ? undefined : `quick-ring-${lit.key}`}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            data-testid="quick-ring"
            data-ring-name={spec.ring}
          >
            <svg
              className={styles.wedges}
              viewBox={`0 0 ${String(SIZE)} ${String(SIZE)}`}
              aria-hidden="true"
            >
              {actions.map((action, i) => (
                <path
                  key={action.key}
                  d={wedge(i, actions.length)}
                  fillRule="evenodd"
                  className={styles.wedge}
                  data-active={i === active ? "true" : undefined}
                  data-danger={action.danger === true ? "true" : undefined}
                  data-picked={arranging && picked === i ? "true" : undefined}
                  onPointerEnter={() => setActive(i)}
                  onPointerDown={() => arranging && setDragFrom(i)}
                  onPointerUp={() => {
                    if (arranging && dragFrom !== null && dragFrom !== i) {
                      moveAction(dragFrom, i);
                      setPicked(null);
                    }
                    setDragFrom(null);
                  }}
                  onClick={() => runAt(i)}
                />
              ))}
            </svg>

            {actions.map((action, i) => {
              const c = centreOf(i, actions.length);
              return (
                <div
                  key={action.key}
                  id={`quick-ring-${action.key}`}
                  role="menuitem"
                  aria-keyshortcuts={String(i + 1)}
                  className={styles.item}
                  data-active={i === active ? "true" : undefined}
                  data-danger={action.danger === true ? "true" : undefined}
                  style={
                    actions.length === 1
                      ? { left: SIZE / 2, top: SIZE / 2 - (OUTER + INNER) / 2 }
                      : { left: c.x, top: c.y }
                  }
                  data-testid={`quick-ring-${action.key}`}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d={action.icon} />
                  </svg>
                  <span>{action.label}</span>
                  <span className={styles.key} aria-hidden="true">
                    {i + 1}
                  </span>
                </div>
              );
            })}

            <button
              type="button"
              className={styles.hub}
              onClick={close}
              aria-label="Close quick actions"
              data-critical={patient?.critical != null ? "true" : undefined}
            >
              <span className={styles.hubName}>
                {patient?.critical != null && <span aria-hidden="true">⚠ </span>}
                {spec.title}
              </span>
              {spec.meta !== undefined && <span className={styles.hubMeta}>{spec.meta}</span>}
              <span className={styles.hubHint}>
                {arranging
                  ? picked === null
                    ? "Click an action, then where it goes"
                    : "Now click where it goes"
                  : lit?.hint}
              </span>
            </button>

            <div className={styles.under}>
              {patient?.critical != null && (
                <p className={styles.alert} data-testid="quick-ring-alert">
                  ⚠ {patient.critical}
                </p>
              )}
              {patient?.owes != null && (
                <p className={styles.owes} data-testid="quick-ring-owes">
                  Owes {patient.owes}
                </p>
              )}
              {all.length >= 2 &&
                (arranging ? (
                  <div className={styles.arrange}>
                    <button
                      type="button"
                      onClick={finishArranging}
                      data-testid="quick-ring-arrange-done"
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      onClick={backToDefault}
                      data-testid="quick-ring-arrange-reset"
                    >
                      Back to default
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.arrangeLink}
                    onClick={() => {
                      setMore(false);
                      setArranging(true);
                      ring.current?.focus();
                    }}
                    data-testid="quick-ring-arrange"
                  >
                    Arrange this ring
                  </button>
                ))}
            </div>

            {more && (
              <div className={styles.more} role="group" aria-label="More actions">
                {overflow.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    className={styles.moreItem}
                    data-danger={action.danger === true ? "true" : undefined}
                    onClick={() => runOverflow(action)}
                    data-testid={`quick-ring-more-${action.key}`}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d={action.icon} />
                    </svg>
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {run !== null && (
        <RingRunPanel
          run={run.view}
          actions={run.actions}
          onChange={(view) => setRun({ view, actions: run.actions })}
          onClose={() => setRun(null)}
        />
      )}

      <div className={styles.notice} role="status" aria-live="polite" hidden={notice === null}>
        {notice}
      </div>
    </>
  );
}
